import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Input, Button, Card, Typography, Space } from 'antd';
import moment from 'moment';

import { Form } from '~/components';
import { phoneValidation } from '~/utils';
import {
  useBeginSmsOptMutation,
  useConfirmSmsOtpCodeMutation,
  useSmsOtpState,
  useResendOtpCodeState,
  useInvitationPageState,
  useConfirmInvitationEmail,
} from '~/hooks/invitation';
import { useInterval } from '~/hooks';
import { useIdentity } from '~/hooks/identity';
import CaptchaInput from '~/components/inputs/CaptchaInput';
import features from '~/featuresWithDefaults';
import PhoneInput from '~/components/PhoneInput';

const { Title, Text } = Typography;

const SmsOtp: FC = () => {
  const [state] = useSmsOtpState();

  const { t } = useTranslation('invitation');

  const [pageState, { smsOtpSuccess }] = useInvitationPageState();
  const businessName = pageState.data?.businessLegalName;

  const [beginOtpForm] = Form.useForm();
  const [confirmOtpForm] = Form.useForm();

  const beginSmsOtpMutation = useBeginSmsOptMutation();

  const url = new URL(window.location.href);
  const token = url.searchParams.get('token') as string;

  const onBeginOtpFinish = async (formData: { phoneNumber: string; captchaToken?: string }) => {
    const { captchaToken } = formData;
    if (pageState.data.phoneNumber) {
      await beginSmsOtpMutation.mutateAsync({ invitationToken: token, captchaToken });
    } else {
      const { phoneNumber } = formData;
      await beginSmsOtpMutation.mutateAsync({ phoneNumber, invitationToken: token, captchaToken });
    }
  };

  const confirmSmsOtpCodeMutation = useConfirmSmsOtpCodeMutation();
  const confirmInvitationEmailMutation = useConfirmInvitationEmail();
  const identityQuery = useIdentity();

  const onConfirmOtpFinish = async (formData: { otp: string }) => {
    const { otp } = formData;
    try {
      await confirmSmsOtpCodeMutation.mutateAsync(otp);
      const { data: apiIdentity } = await identityQuery.refetch();
      smsOtpSuccess({ identityHasPassword: !!apiIdentity?.hasPassword });
      if (!apiIdentity?.username) {
        await confirmInvitationEmailMutation.mutateAsync();
      }
    } catch (error: any) {
      //
    }
  };

  const [resendTimer, setResendTimer] = useState(0);
  const [canResendSmsTimestamp, setResendTimestamp] = useResendOtpCodeState();
  const updateTimer = () => {
    const s = moment(canResendSmsTimestamp).diff(moment(), 'seconds');
    setResendTimer(s);
    if (s <= 0) setResendTimestamp(null);
  };
  useInterval(updateTimer, resendTimer || canResendSmsTimestamp ? 333 : null);

  const isOtpError = confirmSmsOtpCodeMutation.isError;
  const [enableAutosubmit, setEnableAutosubmit] = useState(true);
  useEffect(() => {
    setEnableAutosubmit((prevEnableAutosubmit) => {
      if (!!prevEnableAutosubmit === false) return false;
      return !isOtpError;
    });
  }, [isOtpError]);

  const handleAutosubmit = (_: any, otpFormValues: { otp: string }) => {
    const { otp } = otpFormValues;
    if (enableAutosubmit && otp.length === OTP_MIN_LENGTH) {
      confirmOtpForm.submit();
    }
  };

  return (
    <Card className="Invitation-Card">
      <Title className="Invitation-Title" level={2}>
        {t('Hello!')}
      </Title>
      <Space direction="vertical" size={24}>
        <Space direction="vertical" size={16}>
          <Text type="secondary">
            <Trans
              ns="invitation"
              defaults="Welcome to {{businessName}} team! To complete enrolment please enter your phone number. We will send you one-time code via SMS to confirm the phone."
              values={{
                businessName,
              }}
            />
          </Text>
          <Form form={beginOtpForm} layout="vertical" size="large" onFinish={onBeginOtpFinish}>
            {pageState.data.phoneNumber ? (
              <Form.Item name="phoneNumber" initialValue={pageState.data.phoneNumber}>
                <PhoneInput
                  placeholder={t('translation:phoneNumber')}
                  disabled={!!pageState.data.phoneNumber}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="phoneNumber"
                rules={[{ validator: phoneValidation, required: true }]}
              >
                <PhoneInput placeholder={t('translation:phoneNumber')} />
              </Form.Item>
            )}
            {features?.showCaptcha && (
              <Form.Item name="captchaToken" rules={[{ required: true }]}>
                <CaptchaInput />
              </Form.Item>
            )}

            <Form.Item noStyle>
              {resendTimer || beginSmsOtpMutation.isSuccess ? (
                <Button
                  type="dashed"
                  htmlType="submit"
                  disabled={!!resendTimer}
                  loading={beginSmsOtpMutation.isLoading}
                >
                  {resendTimer
                    ? t('Resend code (after {{seconds}}s)', { seconds: resendTimer })
                    : t('Resend code')}
                </Button>
              ) : (
                <Button type="primary" htmlType="submit" loading={beginSmsOtpMutation.isLoading}>
                  {t('Send SMS with one-time code')}
                </Button>
              )}
            </Form.Item>
          </Form>
        </Space>
        {state.challengeToken && canResendSmsTimestamp && (
          <Space direction="vertical" size={16}>
            <Space direction="vertical" size={8}>
              <Text type="secondary">
                {t(
                  'Enter one-time code from SMS. One-time code delivery may take up to one minute. If you haven’t received the code within a minute please request it again.'
                )}
              </Text>
            </Space>
            <Form
              form={confirmOtpForm}
              layout="vertical"
              size="large"
              onFinish={onConfirmOtpFinish}
              onValuesChange={handleAutosubmit}
            >
              <Form.Item name="otp" rules={[{ required: true, min: OTP_MIN_LENGTH }]}>
                <Input placeholder={t('One-time code')} />
              </Form.Item>
              {!enableAutosubmit && (
                <Form.Item noStyle>
                  <Button type="primary" htmlType="submit">
                    {t('Verify')}
                  </Button>
                </Form.Item>
              )}
            </Form>
          </Space>
        )}
      </Space>
    </Card>
  );
};

const OTP_MIN_LENGTH = 5;

export default SmsOtp;
